import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { i18n } from '@/i18n';
import {
  DEFAULT_DISPLAY_FORECAST_MONTH,
  MAX_OF_DISPLAY_FORECAST_MONTH_IN_DIFF_LAST_MONTH_PAGE,
  MIN_OF_DISPLAY_FORECAST_MONTH,
  CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE,
} from '@/customerForecastModule/constants';
import { simpleCopy } from '@/utils/deepCopy';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { CustomerForecastRepository } from '@/coreInfrastructureModule/Repositories/CustomerForecastRepository';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { IYearMonth } from '@/coreDomainsModule/date';
import { ICustomerForecastDiffLastMonthState } from '../domains/entities/customerForecastDiffLastMonthState';
import { BulkGetCustomerForecastDiffLastMonthUseCase } from '../useCases/bulkGetCustomerForecastDiffLastMonthUseCase';
import { ForecastDiffLastMonthService } from '@/corePagesModule/BaseTableContent/components/customerForecast/domains/services/forecastDIffLastMonthService';
import { useRouter } from 'vue-router';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';

export const useCustomerForecastDiffLastMonthStore = defineStore('useCustomerForecastDiffLastMonth', () => {
  const t = i18n.t;
  const router = useRouter();

  /**
   * 表示する内示の月数の選択肢
   */
  const monthsOptionsDisplayForecastNumber = Array(
    MAX_OF_DISPLAY_FORECAST_MONTH_IN_DIFF_LAST_MONTH_PAGE - MIN_OF_DISPLAY_FORECAST_MONTH + 1
  )
    .fill(0)
    .map((_, i) => i + MIN_OF_DISPLAY_FORECAST_MONTH);

  const state = reactive<ICustomerForecastDiffLastMonthState>({
    items: [],
    tableContents: {
      renderingKey: 0,
      tableHeaders: {} as TableContentHeader,
      checkedDisplayIds: [],
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingOptions: initPagingOptions(30),
      sortKey: undefined,
      isAsc: true,
    },
    receivedForecastYearMonth: { year: 0, month: 0 },
    detailFilterModalState: false,
    displayForecastsMonth: DEFAULT_DISPLAY_FORECAST_MONTH,
  });

  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } =
    useTableFilter('customerForecastDiffLastMonth');

  const tableContentItems = computed((): TableContentItem[] | undefined => {
    if (!state.items) return undefined;
    return state.items.map((item) => {
      return {
        displayId: item.businessId ?? '', // businessIdを表示用の一意のIDとして使用する
        isVirtualChild: false,
        ...item,
      };
    });
  });

  /**
   * stateの初期化
   */
  const $reset = () => {
    state.items = [];
    state.tableContents = {
      renderingKey: 0,
      tableHeaders: {} as TableContentHeader,
      checkedDisplayIds: [],
      loadingState: {
        status: {
          type: 'none',
        },
      },
      pagingOptions: initPagingOptions(30),
      sortKey: undefined,
      isAsc: true,
    };
    state.displayForecastsMonth = DEFAULT_DISPLAY_FORECAST_MONTH;
    state.receivedForecastYearMonth = DateService.getBeforeYearMonth(DateService.convertDateToIYearMonth(new Date())); // デフォルトは先月
    state.detailFilterModalState = false;
    state.openedSelectInputType = undefined;
  };

  /**
   * テーブル表示用項目のみリセット
   */
  const resetAllBusinessTableContentItems = () => {
    state.tableContents.tableHeaders = {} as TableContentHeader;
    state.items = [];
    state.tableContents.checkedDisplayIds = [];
    state.tableContents.loadingState = {
      status: {
        type: 'none',
      },
    };
  };

  /**
   * 初期化処理
   * @param repository - 取引情報のリポジトリ
   */
  const init = async (repository: CustomerForecastRepository) => {
    $reset();
    updateDisplayBusinesses(repository);
  };

  /**
   * ヘッダー情報を取得する
   * @param forecastCount - 表示する内示の数
   * @returns ヘッダー情報
   */
  const getTableHeaders = (forecastCount: number): TableContentHeader => {
    return {
      contentProperties: [
        {
          key: 'choice',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: { isSortable: false, isAsc: true },
          propertyPath: { paths: ['choice'], propertyType: 'string' },
        },
        {
          // 客先内示担当者
          key: 'ForecastForReceiptStaff',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['ForecastForReceiptStaff'], propertyType: 'string' },
        },
        {
          // 取引ID
          key: 'businessId',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessId'], propertyType: 'string' },
        },
        {
          // 仕入先CD
          key: 'supplierNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
        },
        {
          // 仕入先名
          key: 'supplierName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['supplierName'], propertyType: 'string' },
        },
        {
          // 製造社
          key: 'manufacturer',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
        },
        {
          // 商品CD
          key: 'businessNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
        },
        {
          // 商品名
          key: 'businessName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['businessName'], propertyType: 'string' },
        },
        {
          // 容量
          key: 'package',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['package'], propertyType: 'number' },
        },
        {
          // 単位
          key: 'unit',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['unit'], propertyType: 'string' },
        },
        {
          // 置場CD
          key: 'warehouseNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
        },
        {
          // 置場名
          key: 'warehouse',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['warehouse'], propertyType: 'string' },
        },
        {
          // エンドユーザー
          key: 'endUser',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['endUser'], propertyType: 'string' },
        },
        {
          // 売上先CD
          key: 'customerNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerNumber'], propertyType: 'string' },
        },
        {
          // 売上先名
          key: 'customerName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerName'], propertyType: 'string' },
        },
        {
          // 送り先CD
          key: 'salesDestinationNumber',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        },
        {
          // 送り先名
          key: 'salesDestinationName',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
        },
        {
          // 販売単価
          key: 'salesUnitPrice',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['salesUnitPrice'], propertyType: 'number' },
        },
        {
          // 更新日時
          key: 'customerForecastUpdateAt',
          isVariableWidth: true,
          isNarrowable: true,
          isEditable: false,
          sortOption: {
            isSortable: true,
            isAsc: true,
          },
          propertyPath: { paths: ['customerForecastUpdateAt'], propertyType: 'string' },
        },
        {
          // 8pxのスペース用
          key: 'Space8px',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          headerWidth: 8,
          propertyPath: { paths: [], propertyType: 'string' },
        },
        // 内示用の列
        ...ForecastDiffLastMonthService.getTableProperty(forecastCount),
        {
          // 内示遍歴ボタン
          key: 'timelineButton',
          isVariableWidth: false,
          isNarrowable: false,
          isEditable: false,
          sortOption: {
            isSortable: false,
            isAsc: true,
          },
          propertyPath: { paths: ['timelineButton'], propertyType: 'string' },
        },
        HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
      ],
      options: {
        stableColumnCount: 0,
        resizableHeaderColumn: false,
      },
      forecastStartYearMonth: DateService.getShiftedIYearMonthByMonths(
        state.receivedForecastYearMonth.year,
        state.receivedForecastYearMonth.month,
        1
      ), // 受領月の翌月
    };
  };

  /**
   * 受領用内示に紐づく取引情報の取得
   * @param repository - 取引情報のリポジトリ
   */
  const fetchDisplayBusinesses = async (repository: CustomerForecastRepository) => {
    state.tableContents.loadingState = {
      status: {
        type: 'loading',
      },
    };

    await initTableFilter();

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE];

    if (savedPreferences) {
      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.tableContents.sortKey = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.tableContents.isAsc = savedPreferences.isAsc;
      }

      // ページサイズ設定の適用
      if (savedPreferences.pageSize) {
        if (state.tableContents.pagingOptions) {
          state.tableContents.pagingOptions.pageSize = savedPreferences.pageSize;
        } else {
          state.tableContents.pagingOptions = {
            ...initPagingOptions(savedPreferences.pageSize),
            pageSize: savedPreferences.pageSize,
          };
        }
      }
    }

    // UIのソート表示を更新
    if (state.tableContents.sortKey) {
      state.tableContents.tableHeaders.contentProperties = state.tableContents.tableHeaders.contentProperties.map((property) => {
        const currentIsAsc = state.tableContents.isAsc ?? true;
        property.sortOption.isAsc = property.key === state.tableContents.sortKey ? currentIsAsc : true;
        return property;
      });
    }

    if (state.receivedForecastYearMonth.year === 0 || state.receivedForecastYearMonth.month === 0) return;

    const useCase = new BulkGetCustomerForecastDiffLastMonthUseCase();
    const sortInfo = state.tableContents.sortKey
      ? ForecastDiffLastMonthService.getSortInfo(state.tableContents.tableHeaders, state.tableContents.sortKey)
      : undefined;
    const result = await useCase.Execute(
      repository,
      state.receivedForecastYearMonth,
      DateService.getShiftedIYearMonthByMonths(state.receivedForecastYearMonth.year, state.receivedForecastYearMonth.month, 1),
      MAX_OF_DISPLAY_FORECAST_MONTH_IN_DIFF_LAST_MONTH_PAGE,
      state.tableContents.pagingOptions,
      sortInfo,
      state.tableContents.isAsc,
      cachedFilterItems.value
    );

    switch (result.status.type) {
      case 'success':
        state.items = result.status.data.result;
        state.tableContents.pagingOptions = result.status.data.pagingOptions;

        if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
          state.tableContents.loadingState.status = {
            type: 'zeroMatch',
          };
        } else {
          state.tableContents.loadingState.status.type = 'success';
        }
        break;
      case 'error':
        state.tableContents.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('データ取得に失敗しました'),
          },
        };
        console.log('error');
        break;
    }
  };

  /**
   * テーブルに表示する取引情報を更新する
   * @param repository - 取引情報のリポジトリ
   */
  const updateDisplayBusinesses = async (repository: CustomerForecastRepository) => {
    resetAllBusinessTableContentItems();
    // 受領用内示が選択されている場合、ヘッダーの更新と取引情報の取得を行う
    state.tableContents.tableHeaders = getTableHeaders(state.displayForecastsMonth);
    await fetchDisplayBusinesses(repository);
    state.tableContents.renderingKey += 1;
  };

  /**
   * 表示する内示の月数を選択した際の処理
   * @param selectedIndex - 選択肢のインデックス
   * @param repository - 取引情報のリポジトリ
   */
  const onSelectedDisplayForecastsMonth = async (selectedIndex: number, repository: CustomerForecastRepository) => {
    if (selectedIndex === undefined) return;
    state.displayForecastsMonth = selectedIndex + MIN_OF_DISPLAY_FORECAST_MONTH;
    // 内示の列数が変わるためヘッダーも更新
    await updateDisplayBusinesses(repository);
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   * @param repository - 取引情報のリポジトリ
   */
  const onChangePaging = async (pagingOptions: IPagingOptions, repository: CustomerForecastRepository) => {
    state.tableContents.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };

    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE]) {
      myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE] = {};
    }
    myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE].pageSize = state.tableContents.pagingOptions.pageSize;
    await savePreferences();

    await fetchDisplayBusinesses(repository);
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   * @param repository - 取引情報のリポジトリ
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, repository: CustomerForecastRepository) => {
    const newIsAsc = sortKey.key === state.tableContents.sortKey ? !state.tableContents.isAsc : false;

    state.tableContents.sortKey = sortKey.key;
    state.tableContents.isAsc = newIsAsc;

    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE]) {
      myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE] = {};
    }
    myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE].sortKey = state.tableContents.sortKey;
    myPreferences[CUSTOMER_FORECAST_DIFF_LAST_MONTH_TABLE_TYPE].isAsc = state.tableContents.isAsc;
    await savePreferences();

    state.tableContents.tableHeaders.contentProperties = state.tableContents.tableHeaders.contentProperties.map((property) => {
      property.sortOption.isAsc = property.key === sortKey.key ? newIsAsc : true;
      return property;
    });
    state.tableContents.pagingOptions.offset = 0;
    await fetchDisplayBusinesses(repository);
  };

  /**
   * 詳細フィルターの適用処理
   * @param repository - 取引情報のリポジトリ
   * @param detailFilterItem - 詳細フィルターの選択内容
   */
  const onAppliedDetailFilter = async (repository: CustomerForecastRepository, detailFilterItem: FilterContentItem[]) => {
    await bulkUpdateFilterInfos(simpleCopy(detailFilterItem));
    state.detailFilterModalState = false;
    await fetchDisplayBusinesses(repository);
  };

  /**
   * 内示の受領月を選択した際の処理
   * @param selectedYearMonth - 選択された年月
   * @param repository - 取引情報のリポジトリ
   */
  const onSelectedReceiveMonth = async (selectedYearMonth: IYearMonth, repository: CustomerForecastRepository) => {
    state.receivedForecastYearMonth = selectedYearMonth;
    if (selectedYearMonth.year === 0 || selectedYearMonth.month === 0) return;
    await updateDisplayBusinesses(repository);
  };

  /**
   * チェックボックスの選択状態を更新する
   * @param checkedDisplayIds - チェックされたアイテムのID
   */
  const onCheckedDisplayIds = (checkedDisplayIds: string[]) => {
    state.tableContents.checkedDisplayIds = checkedDisplayIds;
  };

  /**
   * 内示遍歴ボタンがクリックされた際の処理
   * @param selectedDisplayId - 選択された行のID
   */
  const onClickTransitionTimelineButton = (selectedDisplayId: string | undefined) => {
    // displayIdから取引IDを取得
    const businessId = state.items.find((item) => item.businessId === selectedDisplayId)?.businessId;
    if (!businessId) return;
    // 取引IDを元に内示遍歴画面へ遷移
    router.push({
      name: 'customer-forecast-timeline',
      params: { defaultBusinessId: businessId },
    });
  };

  return {
    monthsOptionsDisplayForecastNumber,
    state,
    $reset,
    tableContentItems,
    resetAllBusinessTableContentItems,
    init,
    getTableHeaders,
    fetchDisplayBusinesses,
    updateDisplayBusinesses,
    onSelectedDisplayForecastsMonth,
    onChangePaging,
    sortTableContentItems,
    onAppliedDetailFilter,
    onSelectedReceiveMonth,
    onCheckedDisplayIds,
    onClickTransitionTimelineButton,
    getFilterItemsValue,
  };
});
